export interface ExtensionReleaseMetadata {
  artifactType: 'extension-release'
  version: string
  versionName: string
  publishedAt: string
  downloadUrl: string
  changelog: string
  minSupportedVersion: string
  requiredReason: string | null
  rollback: {
    version: string
    downloadUrl: string
    sha256: string
  } | null
  files: number
  bytes: number
  sha256: string
  sellerOrigin: string
  rolloutEligible: boolean
  rolloutPercent: number
}

export interface ExtensionReleaseCache {
  metadata: ExtensionReleaseMetadata
  cachedAt: number
}

export interface ExtensionReleaseResult {
  metadata: ExtensionReleaseMetadata | null
  source: 'network' | 'cache' | 'unavailable'
  warning: string | null
}

export type ExtensionReleaseState = 'CURRENT' | 'UPDATE_AVAILABLE' | 'UPDATE_REQUIRED' | 'CHECK_FAILED'

interface ParsedSemver {
  core: [bigint, bigint, bigint]
  prerelease: Array<bigint | string> | null
}

function parseSemver(value: string): ParsedSemver {
  const match = /^(0|[1-9]\d*)\.(0|[1-9]\d*)\.(0|[1-9]\d*)(?:-([0-9A-Za-z-]+(?:\.[0-9A-Za-z-]+)*))?(?:\+[0-9A-Za-z-]+(?:\.[0-9A-Za-z-]+)*)?$/.exec(value)
  if (!match) throw new Error(`版本号不是严格 semver：${value}`)
  const prerelease = match[4]?.split('.').map((part) => {
    if (/^\d+$/.test(part)) {
      if (part.length > 1 && part.startsWith('0')) throw new Error(`预发布版本数字不能有前导零：${value}`)
      return BigInt(part)
    }
    return part
  }) ?? null
  return { core: [BigInt(match[1]!), BigInt(match[2]!), BigInt(match[3]!)], prerelease }
}

export function compareSemver(left: string, right: string): -1 | 0 | 1 {
  const a = parseSemver(left)
  const b = parseSemver(right)
  for (let i = 0; i < 3; i += 1) {
    if (a.core[i]! < b.core[i]!) return -1
    if (a.core[i]! > b.core[i]!) return 1
  }
  if (a.prerelease === null || b.prerelease === null) {
    if (a.prerelease === b.prerelease) return 0
    return a.prerelease === null ? 1 : -1
  }
  const length = Math.max(a.prerelease.length, b.prerelease.length)
  for (let i = 0; i < length; i += 1) {
    const x = a.prerelease[i]
    const y = b.prerelease[i]
    if (x === undefined || y === undefined) return x === undefined ? -1 : 1
    if (x === y) continue
    if (typeof x === 'bigint' && typeof y === 'bigint') return x < y ? -1 : 1
    if (typeof x === 'bigint') return -1
    if (typeof y === 'bigint') return 1
    return x < y ? -1 : 1
  }
  return 0
}

export function extensionReleaseState(currentVersion: string, result: ExtensionReleaseResult): ExtensionReleaseState {
  if (!result.metadata) return 'CHECK_FAILED'
  try {
    if (!result.metadata.rolloutEligible) return 'CURRENT'
    // A stale fallback remains useful as an optional notice, but it must never start a new mandatory block.
    if (!result.warning && compareSemver(currentVersion, result.metadata.minSupportedVersion) < 0) return 'UPDATE_REQUIRED'
    if (compareSemver(currentVersion, result.metadata.version) < 0) return 'UPDATE_AVAILABLE'
    return 'CURRENT'
  } catch {
    return 'CHECK_FAILED'
  }
}

function trustedHttpsOrigin(value: string): string {
  const url = new URL(value)
  if (url.protocol !== 'https:' || url.origin !== value || ['localhost', '127.0.0.1', '::1', '[::1]'].includes(url.hostname)) {
    throw new Error('版本元数据来源不是正式 HTTPS Seller origin')
  }
  return url.origin
}

export function parseExtensionReleaseMetadata(value: unknown, trustedOrigin: string): ExtensionReleaseMetadata {
  const origin = trustedHttpsOrigin(trustedOrigin)
  if (!value || typeof value !== 'object' || Array.isArray(value)) throw new Error('版本元数据格式损坏')
  const raw = value as Record<string, unknown>
  if (raw.artifactType !== 'extension-release') throw new Error('版本元数据类型不正确')
  if (typeof raw.version !== 'string') throw new Error('版本元数据缺少 version')
  parseSemver(raw.version)
  if (typeof raw.versionName !== 'string' || !raw.versionName.trim()) throw new Error('版本元数据缺少 versionName')
  if (typeof raw.publishedAt !== 'string' || !Number.isFinite(Date.parse(raw.publishedAt))) throw new Error('版本元数据缺少有效发布时间')
  if (typeof raw.changelog !== 'string' || !raw.changelog.trim()) throw new Error('版本元数据缺少更新内容')
  if (typeof raw.minSupportedVersion !== 'string') throw new Error('版本元数据缺少最低兼容版本')
  parseSemver(raw.minSupportedVersion)
  if (compareSemver(raw.minSupportedVersion, raw.version) > 0) throw new Error('最低兼容版本不能高于最新版本')
  const requiredReason = raw.requiredReason == null ? null : String(raw.requiredReason).trim()
  if (compareSemver(raw.minSupportedVersion, '0.0.0') > 0 && !requiredReason) throw new Error('强制升级缺少协议或安全原因')
  if (typeof raw.sha256 !== 'string' || !/^[a-f0-9]{64}$/i.test(raw.sha256)) throw new Error('版本元数据哈希不正确')
  if (!Number.isSafeInteger(raw.files) || Number(raw.files) < 1) throw new Error('版本元数据 files 不正确')
  if (!Number.isSafeInteger(raw.bytes) || Number(raw.bytes) < 1) throw new Error('版本元数据 bytes 不正确')
  if (raw.sellerOrigin !== origin) throw new Error('版本元数据来自非官方 Seller origin')
  const rolloutPercent = raw.rolloutPercent == null ? 0 : Number(raw.rolloutPercent)
  if (!Number.isInteger(rolloutPercent) || rolloutPercent < 0 || rolloutPercent > 100) throw new Error('分批比例不正确')
  const rolloutEligible = raw.rolloutEligible === true
  if (typeof raw.downloadUrl !== 'string') throw new Error('版本元数据缺少下载入口')
  const download = new URL(raw.downloadUrl, `${origin}/`)
  const expectedDownloadPath = `/downloads/crossborder-extension-${raw.version}.zip`
  if (download.origin !== origin || download.protocol !== 'https:' || download.pathname !== expectedDownloadPath) {
    throw new Error('下载入口不是官方同源版本化文件')
  }
  let rollback: ExtensionReleaseMetadata['rollback'] = null
  if (raw.rollback != null) {
    if (typeof raw.rollback !== 'object' || Array.isArray(raw.rollback)) throw new Error('回滚元数据格式损坏')
    const candidate = raw.rollback as Record<string, unknown>
    if (typeof candidate.version !== 'string') throw new Error('回滚元数据缺少版本')
    parseSemver(candidate.version)
    if (compareSemver(candidate.version, raw.minSupportedVersion) < 0 || compareSemver(candidate.version, raw.version) >= 0) {
      throw new Error('回滚版本必须处于最低兼容版本与最新版本之间')
    }
    if (typeof candidate.downloadUrl !== 'string') throw new Error('回滚元数据缺少下载入口')
    const rollbackDownload = new URL(candidate.downloadUrl, `${origin}/`)
    if (rollbackDownload.origin !== origin || rollbackDownload.protocol !== 'https:'
        || rollbackDownload.pathname !== `/downloads/crossborder-extension-${candidate.version}.zip`) {
      throw new Error('回滚入口不是官方同源版本化文件')
    }
    if (typeof candidate.sha256 !== 'string' || !/^[a-f0-9]{64}$/i.test(candidate.sha256)) throw new Error('回滚元数据哈希不正确')
    rollback = { version: candidate.version, downloadUrl: rollbackDownload.href, sha256: candidate.sha256.toLowerCase() }
  }
  return {
    artifactType: 'extension-release',
    version: raw.version,
    versionName: raw.versionName.trim(),
    publishedAt: new Date(raw.publishedAt).toISOString(),
    downloadUrl: download.href,
    changelog: raw.changelog.trim(),
    minSupportedVersion: raw.minSupportedVersion,
    requiredReason,
    rollback,
    files: Number(raw.files),
    bytes: Number(raw.bytes),
    sha256: raw.sha256.toLowerCase(),
    sellerOrigin: origin,
    rolloutEligible,
    rolloutPercent,
  }
}

export async function loadExtensionRelease(options: {
  trustedOrigin: string
  fetcher?: typeof fetch
  timeoutMs?: number
  cacheTtlMs?: number
  now?: () => number
  readCache?: () => Promise<ExtensionReleaseCache | null>
  writeCache?: (cache: ExtensionReleaseCache) => Promise<void>
  installationId?: string
  currentVersion?: string
}): Promise<ExtensionReleaseResult> {
  let origin: string
  try {
    origin = trustedHttpsOrigin(options.trustedOrigin)
  } catch (error) {
    return { metadata: null, source: 'unavailable', warning: error instanceof Error ? error.message : String(error) }
  }
  const now = options.now ?? Date.now
  const ttl = options.cacheTtlMs ?? 5 * 60 * 1000
  let cached: ExtensionReleaseCache | null = null
  try {
    const raw = await options.readCache?.()
    if (raw) cached = { metadata: parseExtensionReleaseMetadata(raw.metadata, origin), cachedAt: raw.cachedAt }
  } catch {
    cached = null
  }
  if (cached && Number.isFinite(cached.cachedAt) && now() - cached.cachedAt >= 0 && now() - cached.cachedAt < ttl) {
    return { metadata: cached.metadata, source: 'cache', warning: null }
  }

  const controller = new AbortController()
  const timer = setTimeout(() => controller.abort(), options.timeoutMs ?? 3500)
  try {
    const url = new URL(`${origin}/public/v1/extension-release/latest`)
    if (options.installationId) url.searchParams.set('installationId', options.installationId)
    if (options.currentVersion) url.searchParams.set('installedVersion', options.currentVersion)
    const response = await (options.fetcher ?? fetch)(url, {
      cache: 'no-store',
      credentials: 'omit',
      signal: controller.signal,
    })
    if (response.status === 204) {
      return { metadata: null, source: 'unavailable', warning: '当前没有已激活的插件版本，安装包下载与首次设备授权暂不可用；已有授权和草稿不会被清除。' }
    }
    if (!response.ok) throw new Error(`版本元数据请求失败（HTTP ${response.status}）`)
    const metadata = parseExtensionReleaseMetadata(await response.json(), origin)
    await options.writeCache?.({ metadata, cachedAt: now() })
    return { metadata, source: 'network', warning: null }
  } catch (error) {
    const detail = error instanceof Error && error.name === 'AbortError'
      ? '版本检查超时'
      : `版本检查暂不可用：${error instanceof Error ? error.message : String(error)}`
    const warning = cached
      ? `${detail}；当前显示已核验的缓存版本，请稍后重试。`
      : `${detail}；安装包下载与首次设备授权暂不可用，请稍后重试。`
    return cached
      ? { metadata: cached.metadata, source: 'cache', warning }
      : { metadata: null, source: 'unavailable', warning }
  } finally {
    clearTimeout(timer)
  }
}
